// frontend/src/components/billing/queries.ts
// TanStack Query hooks for the billing surfaces (Batch B, plan 2026-06-30).
// Org-scoped billing addressing (spec 2026-07-02 D5): EVERY billing endpoint
// is /organizations/:orgId/billing/* — the org id comes from the URL, keyed
// with qk.org(...). No X-Workspace-Id, no active-workspace dependency: the
// org billing page renders purely from the URL's org.
// Every write invalidates the read keys it affects, so sibling billing
// components (Billing page cards, Plans page, org sidebar) refresh together.
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useApiMutation,
  useApiQuery,
  useApiQueryWithTransport,
} from '@/hooks/use-api-query';
import { useFetch } from '@/hooks/use-fetch';
import { qk } from '@/lib/query/keys';
import type { BillingSubscription, OrgUsage, OverflowMode } from '@/types/billing';

// Re-exported so existing call sites importing the type from this module
// (the query hooks below) keep working — types/billing.ts is the canonical
// definition (shared with workspace-context.tsx's OrgUsageData).
export type { OrgUsage } from '@/types/billing';

// ── Subscription (org-scoped) ────────────────────────────────────────────────

export function useSubscriptionQuery(orgId: string | null) {
  return useApiQuery<BillingSubscription>(
    qk.org(orgId ?? '', 'billing', 'subscription'),
    `/organizations/${orgId}/billing/subscription`,
    { enabled: !!orgId },
  );
}

/** One invalidation for every write that changes the subscription read model
 *  (tier switch, reactivate, overflow, checkout return). Both BillingSection
 *  and PlansSection observe the same key, so a single invalidate refreshes
 *  both surfaces. */
export function useSubscriptionInvalidate(orgId: string | null) {
  const qc = useQueryClient();
  return useCallback(
    () =>
      orgId
        ? Promise.all([
            qc.invalidateQueries({
              queryKey: qk.org(orgId, 'billing', 'subscription'),
            }),
            // The usage limit is derived from the subscription's plan quota, so a
            // plan change must refresh usage too — otherwise the meter keeps the
            // old plan's limit (e.g. upgrade Build shows 30k in the header but the
            // meter still reads 2k). Prefix match also refreshes the sidebar meter.
            qc.invalidateQueries({ queryKey: qk.org(orgId, 'usage') }),
            // Workspace rows display usage from the same quota period.
            qc.invalidateQueries({ queryKey: qk.org(orgId, 'workspaces') }),
            // Workspace-scoped usage (/webhook/usage) derives limit, overflowMode
            // and reserve from the same subscription; it powers the sidebar usage
            // banner, threshold toasts, and the top-up wallet. Without this, the
            // "usage at 100%" banner survives an upgrade until a hard reload.
            qc.invalidateQueries({
              predicate: (q) =>
                q.queryKey[0] === 'workspace' && q.queryKey[2] === 'usage',
            }),
            // Channels carry forwardingDisabledReason='usage_limit' while the org
            // is frozen; the backend clears it on upgrade (recoverOrg via Stripe
            // webhook). The sidebar's frozen banner derives from the channels
            // query, so refresh it too or the red alert outlives the upgrade.
            qc.invalidateQueries({
              predicate: (q) =>
                q.queryKey[0] === 'workspace' && q.queryKey[2] === 'channels',
            }),
          ]).then(() => undefined)
        : Promise.resolve(),
    [qc, orgId],
  );
}

// ── Org usage meter ──────────────────────────────────────────────────────────
// OrgUsage's canonical definition lives in @/types/billing (shared with
// workspace-context.tsx's OrgUsageData); re-exported above.

/** Plans-page meter. Unsuppressed like the effect it replaces: a 5xx GET still
 *  routes to the global error page via use-fetch. */
export function useOrgUsageQuery(orgId: string | null) {
  return useApiQuery<OrgUsage>(
    qk.org(orgId ?? '', 'usage'),
    `/organizations/${orgId}/usage`,
    { enabled: !!orgId },
  );
}

/**
 * AIT-629: a dashboard left open across the quota window end keeps the old
 * period start forever (no polling, focus refetch off), so the
 * "Billing period" range would silently span two periods. Refetch the usage
 * queries at the boundary, then every minute until the boundary moves.
 */
export function useQuotaWindowRollover(resetsAt: string | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!resetsAt) return;
    const boundary = new Date(resetsAt).getTime();
    if (!Number.isFinite(boundary)) return;
    // setTimeout overflows past 2^31-1 ms (~24.8 days) and a monthly window can
    // be 31 days, so chain: sleep at most the ceiling, re-check, repeat.
    const MAX_DELAY = 2_147_483_647;
    // Once past the boundary, retry every minute until the usage response
    // carries a new resetsAt (that changes the deps, which clears the timer).
    // Covers a failed refetch (retry: false) and a stale cached boundary.
    const RETRY_MS = 60_000;
    let timer: ReturnType<typeof setTimeout>;
    let disposed = false;
    let attempts = 0;
    const arm = () => {
      if (disposed) return; // unmounted while a refetch was in flight
      const ms = boundary - Date.now();
      // +1s so the server has certainly rolled the window over before we ask.
      // An already-elapsed boundary (stale cache on mount) refetches at once;
      // only the retries after an unchanged/failed refresh wait a minute.
      const delay = ms > 0 ? Math.min(ms + 1_000, MAX_DELAY) : attempts === 0 ? 0 : RETRY_MS;
      timer = setTimeout(() => {
        if (Date.now() < boundary) return arm();
        attempts += 1;
        void qc
          .invalidateQueries({ predicate: (q) => q.queryKey.includes('usage') })
          .finally(arm);
      }, delay);
    };
    arm();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [qc, resetsAt]);
}

/** Org-sidebar meter. Non-critical chrome: keeps the suppressGlobalError
 *  fail-soft, so it needs its own transport and its own cache entry — the
 *  error-escalation behavior differs from the Plans meter on the same
 *  endpoint (and the two surfaces fetched independently before Query too). */
export function useSidebarOrgUsageQuery(orgId: string | null) {
  const fetchWithAuth = useFetch();
  const transport = useCallback(
    (path: string, init?: RequestInit) =>
      fetchWithAuth(path, init, { suppressGlobalError: true }),
    [fetchWithAuth],
  );
  return useApiQueryWithTransport<OrgUsage>(
    qk.org(orgId ?? '', 'usage', 'sidebar'),
    `/organizations/${orgId}/usage`,
    transport,
    { enabled: !!orgId },
  );
}

// ── v2 eligibility (org-scoped) ──────────────────────────────────────────────

/** AIT-420 — which v2 plan this org has earned. `eligiblePlan` is MEASURED
 *  from usage, never chosen, so the checkout card offers exactly one tier.
 *  400 PLAN_NOT_AVAILABLE while the money-model-v2 flag is off; callers treat
 *  an error as "stay on the legacy view". No retry: a 400 won't heal. */
export interface BillingEligibility {
  eligiblePlan: 'build' | 'scale' | 'business';
  trialActions: number;
  trialStatus: 'not_started' | 'active' | 'expired';
}

export function useEligibilityQuery(orgId: string | null, enabled: boolean) {
  return useApiQuery<BillingEligibility>(
    qk.org(orgId ?? '', 'billing', 'eligibility'),
    `/organizations/${orgId}/billing/eligibility`,
    { enabled: !!orgId && enabled, retry: false },
  );
}

// ── Billing details (org-scoped) ─────────────────────────────────────────────

export interface BillingDetailsData {
  email: string | null;
  name: string | null;
  address: {
    country: string | null;
    line1: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  } | null;
}

export function useBillingDetailsQuery(orgId: string) {
  return useApiQuery<BillingDetailsData>(
    qk.org(orgId, 'billing', 'details'),
    `/organizations/${orgId}/billing/billing-details`,
  );
}

export function useBillingDetailsInvalidate(orgId: string) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: qk.org(orgId, 'billing', 'details') }),
    [qc, orgId],
  );
}

// ── Saved cards (org-scoped) ─────────────────────────────────────────────────

export interface PaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
}

export function usePaymentMethodsQuery(orgId: string) {
  return useApiQuery<PaymentMethod[]>(
    qk.org(orgId, 'billing', 'payment-methods'),
    `/organizations/${orgId}/billing/payment-methods`,
  );
}

export function usePaymentMethodsInvalidate(orgId: string) {
  const qc = useQueryClient();
  return useCallback(
    () =>
      qc.invalidateQueries({
        queryKey: qk.org(orgId, 'billing', 'payment-methods'),
      }),
    [qc, orgId],
  );
}

export function useSetDefaultCard(orgId: string) {
  const invalidate = usePaymentMethodsInvalidate(orgId);
  return useApiMutation<void, { paymentMethodId: string }>(
    (vars) => ({
      path: `/organizations/${orgId}/billing/payment-method`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Could not update default. Please try again.',
    }),
    { onSuccess: invalidate },
  );
}

export function useRemoveCard(orgId: string) {
  const invalidate = usePaymentMethodsInvalidate(orgId);
  return useApiMutation<void, { paymentMethodId: string }>(
    ({ paymentMethodId }) => ({
      path: `/organizations/${orgId}/billing/payment-method/${paymentMethodId}`,
      init: { method: 'DELETE' },
      fallback: 'Could not remove the card.',
    }),
    { onSuccess: invalidate },
  );
}

/** AIT-505 — what the add-card dialog attributes this SetupIntent to.
 *  `targetSlug`/`interval` carry a plan pick through the paused-resume /
 *  trial-swap flows so the mint, the notice copy, and the eventual charge all
 *  quote the SAME plan; `source` attributes which surface opened the dialog. */
export interface MintSetupIntentVars {
  targetSlug?: 'build' | 'scale' | 'business';
  interval?: 'monthly' | 'annual';
  source?: 'alert' | 'plans' | 'settings';
}

/** Mint a SetupIntent for the add-card dialog. A mutation, not a query: the
 *  secret must be re-minted fresh on every dialog open (and again on a
 *  charge_failed outcome) — caching one would be wrong. */
export function useMintSetupIntent(orgId: string) {
  return useApiMutation<{ clientSecret: string }, MintSetupIntentVars>((vars) => ({
    path: `/organizations/${orgId}/billing/setup-intent`,
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vars),
    },
  }));
}

// ── Invoice history (org-scoped) ─────────────────────────────────────────────

export interface Invoice {
  id: string;
  number: string | null;
  created: number;
  amountCents: number;
  currency: string;
  status: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
}

export interface InvoicePage {
  items: Invoice[];
  nextCursor: string | null;
}

export function useInvoicesQuery(orgId: string) {
  return useApiQuery<InvoicePage>(
    qk.org(orgId, 'billing', 'invoices'),
    `/organizations/${orgId}/billing/invoices`,
  );
}

// ponytail: Load More is modeled as a mutation (a fetch-more action) so the
// accumulated-pages state stays component-local; switch to useInfiniteQuery if
// more surfaces ever need the full paginated list.
export function useLoadMoreInvoices(orgId: string) {
  return useApiMutation<InvoicePage, { startingAfter: string }>(
    ({ startingAfter }) => ({
      path: `/organizations/${orgId}/billing/invoices?starting_after=${encodeURIComponent(startingAfter)}`,
    }),
  );
}

// ── Subscription lifecycle writes ────────────────────────────────────────────

/** Clears a scheduled cancel (Keep Plan) or scheduled downgrade (Cancel
 *  Scheduled Change) via the org billing reactivate endpoint. */
export function useReactivate(orgId: string | null) {
  const invalidate = useSubscriptionInvalidate(orgId);
  return useApiMutation<void, void>(
    () => ({
      path: `/organizations/${orgId}/billing/reactivate`,
      init: { method: 'POST' },
    }),
    { onSuccess: invalidate },
  );
}

export function useCheckout(orgId: string | null) {
  return useApiMutation<
    { url: string },
    { planSlug: string; billingInterval: 'monthly' | 'annual' }
  >((vars) => ({
    path: `/organizations/${orgId}/billing/checkout`,
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vars),
    },
    fallback: 'Something went wrong. Please try again.',
  }));
}

/** POST /organizations/:orgId/billing/trial-plan — AIT-420 Task 8: swap the
 *  trialing org's plan in place (no proration, trial_end untouched). Carries
 *  the Monthly/Yearly toggle the cards are priced against. Allowed only while
 *  status is 'trialing'. */
export function useSwapTrialPlan(orgId: string | null) {
  const invalidate = useSubscriptionInvalidate(orgId);
  return useApiMutation<
    { planSlug: string },
    { slug: 'build' | 'scale' | 'business'; interval?: 'monthly' | 'annual' }
  >(
    (vars) => ({
      path: `/organizations/${orgId}/billing/trial-plan`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Could not switch plans. Please try again.',
    }),
    { onSuccess: invalidate },
  );
}

/** GET /organizations/:orgId/billing/latest-invoice-url — AIT-420 Task 8: the
 *  open invoice a past_due/unpaid org can pay directly. A mutation (not a
 *  query) since it's fired on click, not rendered. */
export function useLatestInvoiceUrl(orgId: string | null) {
  return useApiMutation<{ url: string }, void>(() => ({
    path: `/organizations/${orgId}/billing/latest-invoice-url`,
    fallback: 'Could not load your invoice. Please try again.',
  }));
}

export function useUsageTierChange(orgId: string | null) {
  const invalidate = useSubscriptionInvalidate(orgId);
  return useApiMutation<
    { scheduled: boolean },
    { planSlug: string; billingInterval: 'monthly' | 'annual' }
  >(
    (vars) => ({
      path: `/organizations/${orgId}/billing/usage-tier`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Something went wrong. Please try again.',
    }),
    { onSuccess: invalidate },
  );
}

/** Proration preview for the upgrade confirm dialog. A mutation (POST read):
 *  the amount is dialog-transient and must be computed fresh per open. */
export function useUsageTierPreview(orgId: string | null) {
  return useApiMutation<
    { scheduled: boolean; amountDueCents?: number },
    { planSlug: string; billingInterval: 'monthly' | 'annual' }
  >((vars) => ({
    path: `/organizations/${orgId}/billing/usage-tier/preview`,
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vars),
    },
  }));
}

/** PUT /organizations/:orgId/billing/overflow. Vars carry the pre-built body
 *  verbatim so the pending-choice replay (overflow-switch localStorage stash)
 *  sends the exact payload the user approved. */
export interface SetOverflowBody {
  mode: OverflowMode;
  consent: boolean;
  rechargeAmountMessages?: number;
  thresholdPercent?: number;
  spendCapCents?: number;
}

export function useSetOverflow(orgId: string | null) {
  const invalidate = useSubscriptionInvalidate(orgId);
  return useApiMutation<void, { body: SetOverflowBody | unknown }>(
    ({ body }) => ({
      path: `/organizations/${orgId}/billing/overflow`,
      init: {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      fallback: 'Could not save',
    }),
    { onSuccess: invalidate },
  );
}

// ── Manage Subscription read model ───────────────────────────────────────────

export interface ManageSubscriptionModel {
  usage?: { slug: string; name: string; priceCents: number; custom?: boolean };
  pending?: { kind: 'usageDowngrade' | 'cancelAll'; effectiveAt: string } | null;
}

/** Loaded per dialog open. staleTime 0 so every reopen refetches (the pending
 *  teardown state must be fresh after a cancel). */
export function useManageSubscriptionQuery(
  orgId: string | undefined,
  open: boolean,
) {
  return useApiQuery<ManageSubscriptionModel>(
    qk.org(orgId ?? '', 'billing', 'manage-subscription'),
    `/organizations/${orgId}/billing/manage-subscription`,
    { enabled: open && !!orgId, staleTime: 0 },
  );
}
